import { useCallback, useEffect, useRef } from "react";
import { useDataProvider, useGetIdentity } from "ra-core";
import { useLocation } from "react-router";
import { useQuery } from "@tanstack/react-query";

import { supabase } from "@/components/atomic-crm/providers/supabase/supabase";
import type { CrmDataProvider } from "@/components/atomic-crm/providers/types";
import { useMessagesQuickAccess } from "@/modules/messages/messagesQuickAccessContext";
import { useNotificationPrefsContext } from "@/modules/notifications/NotificationPrefsContext";
import {
  getNotificationHistory,
  markNotificationHistoryRead,
  removeNotificationHistoryItem,
  setNotificationHistory,
  upsertNotificationHistory,
  type MemberNotificationRow,
} from "@/modules/notifications/notificationHistory";
import { TICKET_WORKSPACE_SETTINGS_QUERY_KEY } from "@/modules/settings/tickets/useTicketWorkspaceSettings";
import { DEFAULT_TICKET_WORKSPACE_SETTINGS } from "@/modules/settings/tickets/ticketWorkspaceSettings";
import { isNotificationLeader } from "@/modules/notifications/notificationLeader";
import { isViewingNotificationTarget } from "@/modules/notifications/memberNotificationModel";

const COLUMNS =
  "id, category, title, body, href, tag, occurrences, created_at, read_at";

const isTabFocused = () =>
  document.visibilityState === "visible" && document.hasFocus();

/**
 * The bell, from the server: loads the member's notifications, keeps them
 * live (Realtime) and alerts for new ones. Only one tab plays sounds and
 * desktop alerts; nothing alerts for what the focused tab already shows.
 */
export const useServerNotifications = () => {
  const { identity } = useGetIdentity();
  const location = useLocation();
  const { activeConversationId } = useMessagesQuickAccess();
  const { pushNotification } = useNotificationPrefsContext();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const viewRef = useRef({ location, activeConversationId });
  viewRef.current = { location, activeConversationId };

  const { data: ticketSettings } = useQuery({
    queryKey: TICKET_WORKSPACE_SETTINGS_QUERY_KEY,
    queryFn: () => dataProvider.getTicketWorkspaceSettings(),
    staleTime: 60_000,
  });
  const workspace =
    ticketSettings?.workspace ?? DEFAULT_TICKET_WORKSPACE_SETTINGS;
  const workspaceRef = useRef(workspace);
  workspaceRef.current = workspace;

  const load = useCallback(async () => {
    if (!identity?.id) return;
    const { data } = await supabase
      .from("member_notifications")
      .select(COLUMNS)
      .eq("recipient_member_id", identity.id)
      .order("created_at", { ascending: false })
      .limit(100);
    if (data) setNotificationHistory(data as MemberNotificationRow[]);
  }, [identity?.id]);

  const alert = useCallback(
    (row: MemberNotificationRow) => {
      const entry = upsertNotificationHistory(row);
      if (entry.read) return;
      const focused = isTabFocused();
      const { location: here, activeConversationId: active } = viewRef.current;
      if (focused && isViewingNotificationTarget(entry, here, active)) {
        // Already on screen: no alert, and it is read.
        markNotificationHistoryRead(entry.id);
        return;
      }
      const ticket =
        entry.category === "tickets_message" ||
        entry.category === "tickets_assigned";
      const leader = isNotificationLeader();
      pushNotification({
        entry,
        // One tab sounds; the focused tab shows the preview card.
        sound:
          leader && (!ticket || workspaceRef.current.workspace_notify_sound),
        desktop:
          leader &&
          !focused &&
          (!ticket || workspaceRef.current.workspace_notify_desktop),
        preview: focused,
      });
    },
    [pushNotification],
  );

  useEffect(() => {
    if (!identity?.id) return;
    // The bell used to live in this browser only; the server has it now.
    try {
      window.localStorage.removeItem("nomi.notificationHistory");
    } catch {
      /* storage unavailable */
    }
    void load();

    const channel = supabase
      .channel(`member_notifications_${identity.id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "member_notifications",
          filter: `recipient_member_id=eq.${identity.id}`,
        },
        (payload) => alert(payload.new as MemberNotificationRow),
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "member_notifications",
          filter: `recipient_member_id=eq.${identity.id}`,
        },
        (payload) => {
          const row = payload.new as MemberNotificationRow;
          const known = getNotificationHistory().find(
            (item) => item.id === String(row.id),
          );
          // Bumped (e.g. another message in the same conversation) →
          // alert again; read on another device → just update the bell.
          if (row.read_at == null && known?.created_at !== row.created_at) {
            alert(row);
          } else {
            upsertNotificationHistory(row);
          }
        },
      )
      .on(
        "postgres_changes",
        { event: "DELETE", schema: "public", table: "member_notifications" },
        (payload) => {
          // DELETE events carry only the id (and cannot be filtered).
          const old = payload.old as Partial<MemberNotificationRow>;
          if (old.id != null) removeNotificationHistoryItem(String(old.id));
        },
      )
      .subscribe((status) => {
        // Catch up on anything missed while disconnected.
        if (status === "SUBSCRIBED") void load();
      });

    const refresh = () => {
      if (document.visibilityState === "visible") void load();
    };
    document.addEventListener("visibilitychange", refresh);
    window.addEventListener("online", refresh);
    return () => {
      document.removeEventListener("visibilitychange", refresh);
      window.removeEventListener("online", refresh);
      void supabase.removeChannel(channel);
    };
  }, [identity?.id, load, alert]);
};
